import DataGrid, {
    KeyboardNavigation,
    Pager,
    Paging,
    SearchPanel,
    Export,
    Column,
    MasterDetail
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { pageFormats, allowedPageSizes } from '../../components/GlobalDataGridConfigurations';
import CustomPreparedHeaderCell from '../../components/CustomPreparedHeaderCell';
import InfoIconComponent from '../../components/CustomInfoIcon';
import { FOUOheader, FOUOfooter } from '../../components/FOUO';
import { useAuth } from '../../hooks/useAuth';
import ExportGrid from '../../components/ExportGrid';
import dayjs from 'dayjs';

const formatAddress = (addr) => {
    if (!addr) return "N/A";
    return `${addr.Address1 ?? ""} ${addr.Address2 ?? ""}, ${addr.CityName ?? ""} ${addr.State ?? ""} ${addr.ZipCode ?? ""}`.trim();
};

const formatBuildings = (buildings) => {
    if (buildings && buildings.length > 0) {
        // Create a comma-separated list of SequenceNumber values
        const buildingList = buildings
            .map(building => building.SequenceNumber)
            .filter(seq => seq !== undefined && seq !== null)
            .join(", ");
        return buildingList || "N/A";
    }
    return "N/A";
};

const CustomerChanges = ({ data }) => {
    const { user } = useAuth();

    const dataSource = new CustomStore({
        key: 'AuditTrailNumber',
        load: async () => {
            // console.log("customer data", data);
            return data.map(element => ({
                AuditTrailNumber: element.AuditTrailNumber ?? "N/A",
                ChangeType: element.ChangeType ?? "N/A",
                DateChanged: element.DateChanged ? dayjs(element.DateChanged).utc().format("MM/DD/YYYY h:mma") : "N/A",
                UpdatedBy: element.user ?? "N/A",
                initial: {
                    AbCode: element.InitialValue?.AgencyBureauCode ?? "N/A",
                    ActualAbCode: element.InitialValue?.ActualAgencyBureauCode ?? "N/A",
                    Address: formatAddress(element.InitialValue),
                    AgencyBureauName: element.InitialValue?.AgencyBureauName ?? "N/A",
                    AgencyLocationCode: element.InitialValue?.AgencyLocationCode ?? "N/A",
                    AgencyName: element.InitialValue?.AgencyBureauName ?? "N/A",
                    Buildings: formatBuildings(element.InitialValue?.Buildings),
                    BilledABCode: element.InitialValue?.BilledAgencyBureauCode ?? "N/A",
                    BusinessEventTypeCode: element.InitialValue?.BusinessEventTypeCode ?? "N/A",
                    CustomerPOCFirstName: element.InitialValue?.CustomerPOCFirstName ?? "N/A",
                    CustomerPOCLastName: element.InitialValue?.CustomerPOCLastName ?? "N/A",
                    FundingDocumentNumber: element.InitialValue?.FundingDocumentNumber ?? "N/A",
                    POCsToRecieveSummary: element.InitialValue?.POCsToRecieveSummary ?? "N/A",
                    SendIPAC: element.InitialValue?.SendIPAC ?? "N/A",
                    ShortName: element.InitialValue?.AgencyBureauShortName ?? "N/A",
                    TreasuryAccSymbol: element.InitialValue?.TreasuryAccSymbol ?? "N/A",

                },
                changed: {
                    AbCode: element.ChangedValue?.AgencyBureauCode ?? "N/A",
                    ActualAbCode: element.ChangedValue?.ActualAgencyBureauCode ?? "N/A",
                    Address: formatAddress(element.ChangedValue),
                    AgencyBureauName: element.ChangedValue?.AgencyBureauName ?? "N/A",
                    AgencyLocationCode: element.ChangedValue?.AgencyLocationCode ?? "N/A",
                    AgencyName: element.ChangedValue?.AgencyBureauName ?? "N/A",
                    Buildings: formatBuildings(element.ChangedValue?.Buildings),
                    BilledABCode: element.ChangedValue?.BilledAgencyBureauCode ?? "N/A",
                    BusinessEventTypeCode: element.ChangedValue?.BusinessEventTypeCode ?? "N/A",
                    CustomerPOCFirstName: element.ChangedValue?.CustomerPOCFirstName ?? "N/A",
                    CustomerPOCLastName: element.ChangedValue?.CustomerPOCLastName ?? "N/A",
                    FundingDocumentNumber: element.ChangedValue?.FundingDocumentNumber ?? "N/A",
                    POCsToRecieveSummary: element.ChangedValue?.POCsToRecieveSummary ?? "N/A",
                    SendIPAC: element.ChangedValue?.SendIPAC ?? "N/A",
                    ShortName: element.ChangedValue?.AgencyBureauShortName ?? "N/A",
                    TreasuryAccSymbol: element.ChangedValue?.TreasuryAccSymbol ?? "N/A",

                },
            }));
        }
    });

    const pdfFont = () => 7;
    const wrapPdfCell = () => true;

    return (
        <div style={{ margin: 30 }}>
            <FOUOheader
                dateString={dayjs.utc().format('MM/DD/YYYY h:mma') + " UTC"}
                heading={"Audit - Customer Changes"}
                userEmail={user.email}
            />
            <DataGrid
                columnAutoWidth={true}
                dataSource={dataSource}
                onCellPrepared={CustomPreparedHeaderCell}
                showBorders={true}
                showColumnLines={true}
                showRowLines={true}
                rowAlternationEnabled={true}
                onExporting={e => ExportGrid(e, "Audit_Report", wrapPdfCell(), pdfFont())}
            >

                <Column dataField="AuditTrailNumber" caption="Audit Trail Number" visible={false} />
                <Column dataField="DateChanged" caption="Date Changed (UTC)" />
                <Column dataField="ChangeType" caption="Change Type" />
                <Column dataField="UpdatedBy" caption="Changed By" />

                <Column dataField="changed.AbCode" caption="Agency Bureau Code" />
                <Column dataField="changed.ShortName" caption="Agency Bureau Short Name" />
                <Column dataField="changed.ActualAbCode" caption="Actual Agency Bureau Code" />
                <Column dataField="changed.AgencyBureauName" caption="Agency Bureau Name" />
                <Column dataField="changed.AgencyLocationCode" caption="Agency Location Code" />
                <Column dataField="changed.AgencyName" caption="Agency Name" />
                <Column dataField="changed.Buildings" caption="Buildings" />
                <Column dataField="changed.BilledABCode" caption="Billed Agency Bureau Code" />
                <Column dataField="changed.BusinessEventTypeCode" caption="Business Event Type Code" />
                <Column dataField="changed.CustomerPOCFirstName" caption="Customer POC First Name" />
                <Column dataField="changed.CustomerPOCLastName" caption="Customer POC Last Name" />
                <Column dataField="changed.FundingDocumentNumber" caption="Funding Document Number" />
                <Column dataField="changed.POCsToRecieveSummary" caption="POCs To Receive Summary" />
                <Column dataField="changed.SendIPAC" caption="Send IPAC" />
                <Column dataField="changed.TreasuryAccSymbol" caption="Treasury Account Symbol" />
                <Column dataField="changed.Address" caption="Address" />


                <KeyboardNavigation enabled={true} />
                <SearchPanel visible={true} highlightCaseSensitive={true} />
                <Paging defaultPageSize={10} />
                <Pager showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} />
                <Export enabled={true} formats={pageFormats} />
                <MasterDetail
                    enabled={true}
                    render={({ data }) => RenderTemplate(data)}
                />
            </DataGrid>
            <FOUOfooter />
        </div>
    );
};

const changedComponent = (value) => (
    <>
        {value}
        <InfoIconComponent item={value} key={value} />
    </>
);

const RenderTemplate = (values) => {
    if (values.ChangeType === 'Insert' || !values.initial) {
        return <>No Previous History, record was inserted.</>;
    }

    const renderChangedCell = (field) => (cellInfo) => {
        const prev = cellInfo.data.initial?.[field];
        const curr = cellInfo.data.changed?.[field];

        // If prev is a boolean, convert to string
        if (typeof prev === 'boolean') {
            return prev !== curr ? changedComponent(`${prev}`) : `${prev}`;
        }
        return prev !== curr ? changedComponent(prev) : prev;
    };

    return (
        <DataGrid
            dataSource={[values]}
            showBorders={true}
            columnAutoWidth={true}
        >
            <Column dataField="initial.AbCode" caption="Agency Bureau Code" cellRender={renderChangedCell("AbCode")} />
            <Column dataField="initial.ShortName" caption="Agency Bureau Short Name" cellRender={renderChangedCell("ShortName")} />
            <Column dataField="initial.ActualAbCode" caption="Actual Agency Bureau Code" cellRender={renderChangedCell("ActualAbCode")} />
            <Column dataField="initial.AgencyBureauName" caption="Agency Bureau Name" cellRender={renderChangedCell("AgencyBureauName")} />
            <Column dataField="initial.AgencyLocationCode" caption="Agency Location Code" cellRender={renderChangedCell("AgencyLocationCode")} />
            <Column dataField="initial.AgencyName" caption="Agency Name" cellRender={renderChangedCell("AgencyName")} />
            <Column dataField="initial.Buildings" caption="Buildings" cellRender={renderChangedCell("Buildings")} />
            <Column dataField="initial.BilledABCode" caption="Billed Agency Bureau Code" cellRender={renderChangedCell("BilledABCode")} />
            <Column dataField="initial.BusinessEventTypeCode" caption="Business Event Type Code" cellRender={renderChangedCell("BusinessEventTypeCode")} />
            <Column dataField="initial.CustomerPOCFirstName" caption="Customer POC First Name" cellRender={renderChangedCell("CustomerPOCFirstName")} />
            <Column dataField="initial.CustomerPOCLastName" caption="Customer POC Last Name" cellRender={renderChangedCell("CustomerPOCLastName")} />
            <Column dataField="initial.FundingDocumentNumber" caption="Funding Document Number" cellRender={renderChangedCell("FundingDocumentNumber")} />
            <Column dataField="initial.POCsToRecieveSummary" caption="POCs To Receive Summary" cellRender={renderChangedCell("POCsToRecieveSummary")} />
            <Column dataField="initial.SendIPAC" caption="Send IPAC" cellRender={renderChangedCell("SendIPAC")} />
            <Column dataField="initial.TreasuryAccSymbol" caption="Treasury Account Symbol" cellRender={renderChangedCell("TreasuryAccSymbol")} />
            <Column dataField="initial.Address" caption="Address" cellRender={renderChangedCell("Address")} />
        </DataGrid>
    );
};

export default CustomerChanges;
